"use client";

import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import React from "react";
import type { CommonHtmlProps } from "../../../types/ui";
import { withRefProp } from "../../../utils/withRefProp";
import { OverlayDepthProvider, useOverlayZIndex } from "../OverlayDepth";
import styles from "./FloatingPanel.module.scss";

export type FloatingPanelTheme = "light" | "dark";

export interface FloatingPanelProps extends Omit<CommonHtmlProps, "ref"> {
  ref?: React.Ref<HTMLDivElement>;
  asChild?: boolean;
  theme?: FloatingPanelTheme;
  open?: boolean;
  placement?: string;
  /** When true, the panel will unmount when `open` is false instead of staying mounted with CSS visibility. */
  unmountWhenHidden?: boolean;
}

const DefaultFloatingPanel = withRefProp((props: FloatingPanelProps) => {
  const {
    asChild,
    className,
    style,
    children,
    ref,
    theme = "light",
    open,
    placement,
    unmountWhenHidden,
    ...otherProps
  } = props;
  const zIndex = useOverlayZIndex();
  const [hasBeenOpen, setHasBeenOpen] = React.useState(Boolean(open));
  React.useEffect(() => {
    if (!unmountWhenHidden && open) {
      setHasBeenOpen(true);
    }
  }, [open, unmountWhenHidden]);

  if (unmountWhenHidden && !open) return null;

  const mergedClassName = clsx(styles.panel, className);

  const Component = asChild ? Slot : "div";

  return (
    <OverlayDepthProvider>
      <Component
        ref={ref}
        className={mergedClassName}
        style={{
          ...style,
          zIndex: style?.zIndex ?? zIndex,
        }}
        data-deci-floating-panel="true"
        data-deci-theme={theme}
        data-deci-open={open}
        data-deci-ever-open={unmountWhenHidden ? undefined : hasBeenOpen}
        data-deci-placement={placement}
        {...otherProps}
      >
        {children}
      </Component>
    </OverlayDepthProvider>
  );
});

const FloatingPanel = createPluggable<FloatingPanelProps>("deci.floatingPanel", DefaultFloatingPanel as any);

export default FloatingPanel;
